import _ from 'lodash';
import { formatISO } from 'date-fns';
import { useAuth } from 'src/contexts/JWTContext';
import { queryClient } from 'src/config/react-query';
import { User, TaggedUser, UserDictionaryItem } from 'src/types/user';
import { ChatableType } from 'src/types/chats';
import { ProfileDataMap } from 'src/types/instance';
import { UserPermissions } from 'src/utils/permissions';
import { FieldNamesMarkedBoolean } from 'react-hook-form';
import { PROFILE_DATA_CONSTANTS } from 'src/constants/instance';
import {
  getProfileById,
  getUserListByIds,
  getUserList,
} from 'src/services/users';
import {
  getIssuerRequestsList,
  getUserListSubordinatesForVacations,
} from 'src/services/vacations';
import { userKeys } from 'src/components/dashboard/users/queries';
import { ActionTagUser, KEYS } from 'src/types/user';
import { profileKeys } from 'src/components/dashboard/profile/queries';
import { vacationsKeys } from 'src/pages/dashboard/vacations/queries';

const LIMIT = 20;
const PAGE = 1;


export const getFullName = (
  user: { firstName?: string, lastName?: string },
) => {
  if (!user) return '';

  return [user.firstName, user.lastName].filter((str) => str?.length > 0).join(' ');
};

export const getFullNameUserEvent = (
  { FirstName, LastName }: { FirstName?: string, LastName?: string },
) =>
  [FirstName, LastName].filter((str) => str?.length > 0).join(' ');

export const permissionsObjectToArray = (userPermissions: User['permissions']) => Object.keys(_.pickBy(userPermissions)) as UserPermissions[];

const firstLetterUpper = (word) => word.charAt(0).toUpperCase();

const { USER } = ChatableType;

const CHANNELS = 'CHANNELS';

export const getChatablesForList = (
  chatables,
  oldChatables,
) => {
  let response = [...oldChatables];

  chatables?.forEach(({ type, user, topic }) => {
    const isUser = type === USER;
    const newElem = isUser ? { ...user, type } : { ...topic, type };
    if (response.length === 0) {
      response = [
        {
          character: isUser ? firstLetterUpper(user.firstName) : CHANNELS,
          chatables: [newElem],
        },
      ];
    }
    else if (
      isUser ?
        response[response.length - 1].character === firstLetterUpper(user.firstName)
        : response[response.length - 1].character === CHANNELS
    ) {
      response[response.length - 1].chatables = [
        ...response[response.length - 1].chatables,
        newElem,
      ];
    }
    else {
      response = [
        ...response,
        {
          character: isUser ? firstLetterUpper(user.firstName) : CHANNELS,
          chatables: [newElem],
        },
      ];
    }
  });

  return response;
};

export const getUsersForSectionList = (
  users: User[],
  oldUsers: UserDictionaryItem[] = [],
): UserDictionaryItem[] => {
  let response = [...oldUsers];

  users?.forEach((user) => {
    if (response.length === 0) {
      response = [
        {
          character: firstLetterUpper(user.firstName),
          users: [user],
        },
      ];
    }
    else if (
      response[response.length - 1].character
      === firstLetterUpper(user.firstName)
    ) {
      response[response.length - 1].users = [
        ...response[response.length - 1].users,
        user,
      ];
    }
    else {
      response = [
        ...response,
        {
          character: firstLetterUpper(user.firstName),
          users: [user],
        },
      ];
    }
  });

  return response;
};

export const getInitials = (fullName: string): string => {
  if (!fullName) return '';

  const initalLetters = fullName.split(/(\s+)/).filter((e) => e.trim().length > 0);
  let result = '';

  for (let i = 0; i < Math.min(initalLetters.length, 2); i++) {
    result += initalLetters[i].charAt(0);
  }

  return result.toUpperCase();
};

const CUSTOM_FIELD_DEFAULT_VALUE_MAP = {
  [PROFILE_DATA_CONSTANTS.DATE]: null,
  [PROFILE_DATA_CONSTANTS.STRING]: '',
  [PROFILE_DATA_CONSTANTS.STRING_LIST]: [],
  [PROFILE_DATA_CONSTANTS.USER]: null,
  [PROFILE_DATA_CONSTANTS.USER_LIST]: [],
  [PROFILE_DATA_CONSTANTS.NUMBER]: '',
  [PROFILE_DATA_CONSTANTS.NUMBER_LIST]: [],
  [PROFILE_DATA_CONSTANTS.OPTION]: null,
  [PROFILE_DATA_CONSTANTS.MULTIPLE_OPTION]: [],
};

export const getCustomFieldDefaultValues = (customFields: ProfileDataMap, values) => (
  Object.entries(customFields).reduce((total, current) => ({
    ...total,
    [current[0]]: (values && values[current[0]])
      ?? CUSTOM_FIELD_DEFAULT_VALUE_MAP[current[1].type],
  }), {})
);

export function getDirtyValues<T extends object>(
  dirtyFields: FieldNamesMarkedBoolean<T>,
  allValues: T,
) {
  if (dirtyFields === true || Array.isArray(dirtyFields)) {
    return allValues;
  }

  return Object.fromEntries(
    Object.keys(dirtyFields).map((key) => [key, getDirtyValues(dirtyFields[key], allValues[key])]),
  );
}

const getItemValue = (value, type) => {
  if (type === PROFILE_DATA_CONSTANTS.NUMBER) {
    return parseFloat(value);
  }
  if (type === PROFILE_DATA_CONSTANTS.NUMBER_LIST) {
    return value.map(parseFloat);
  }
  if (type === PROFILE_DATA_CONSTANTS.USER) {
    return value?.id || null;
  }
  if (type === PROFILE_DATA_CONSTANTS.USER_LIST) {
    return value.map((user) => user.id);
  }
  if (type === PROFILE_DATA_CONSTANTS.DATE) {
    return value ? formatISO(value, { representation: 'date' }) : null;
  }
  return value;
};

export const transformCustomFieldValues = (customFields: ProfileDataMap, instanceProfileData) => {
  const initialValue = {};
  Object.entries(customFields).reduce((obj, item) => {
    obj[item[0]] = getItemValue(item[1], instanceProfileData[item[0]].type);
    return obj;
  }, initialValue);
  return initialValue;
};

export const getProfileFieldIdsByType = (profileData: ProfileDataMap, type: string) =>
  Object.entries(profileData)
    .filter((p) => p[1].type === type)
    .map((p) => p[0]);

export const getAllUsersFromProfileData = (userProfileData, userFields, userListFields) => {
  const usersArray = [];
  Object.entries(userProfileData).forEach((item) => {
    if (userFields.includes(item[0]) || userListFields.includes(item[0])) {
      if (Array.isArray(item[1])) {
        item[1].forEach((elem) => usersArray.push(elem));
      }
      else {
        usersArray.push(item[1]);
      }
    }
  });
  return usersArray;
};

export const updateDirtyValuesBeforeSubmit = (initialDirtyValues, profileInstanceData) => {
  const dirtyValues = initialDirtyValues;
  if (dirtyValues.birthdate) {
    dirtyValues.birthdate = formatISO(dirtyValues.birthdate, { representation: 'date' });
  }
  if (dirtyValues.phoneNumber) {
    dirtyValues.phoneNumber = dirtyValues.phoneNumber.length > 4 ? dirtyValues.phoneNumber : null;
  }
  if (dirtyValues.linkedIn === '') {
    dirtyValues.linkedIn = null;
  }
  if (dirtyValues.facebook === '') {
    dirtyValues.facebook = null;
  }
  if (dirtyValues.twitter === '') {
    dirtyValues.twitter = null;
  }
  if (dirtyValues.instagram === '') {
    dirtyValues.instagram = null;
  }
  if (dirtyValues.profileData) {
    dirtyValues.profileData = transformCustomFieldValues(dirtyValues.profileData,
      profileInstanceData);
  }
  return dirtyValues;
};

export const getFullUsersData = async (
  usersData,
  userFields,
  userListFields,
  userProfileData: any[],
) => {
  const response = await getUserListByIds(usersData);
  const relatedUsers = response?.data?.items || [];

  const profileDataWithFullUsers = userProfileData;
  // @ts-ignore
  Object.entries(profileDataWithFullUsers).forEach((item) => {
    if (userFields.includes(item[0])) {
      const userResult = relatedUsers.find((user) => user.id === item[1]);
      profileDataWithFullUsers[item[0]] = userResult;
    }
    if (userListFields.includes(item[0])) {
      const usersResult = [];
      item[1].map((userId) => usersResult.push(relatedUsers.find((user) =>
        user.id === userId)));
      profileDataWithFullUsers[item[0]] = usersResult;
    }
  });

  return profileDataWithFullUsers;
};

export const getTaggedUserData = async (userId: number) => {
  try {
    const response = await queryClient.fetchQuery(
      userKeys.detail(userId),
      () => getProfileById(userId),
      {
        staleTime: 120000,
      },
    );

    return response?.data;
  }
  catch (error) {
    return null;
  }
};

export const getTaggedUserPosition = (user: User, text: string) => (
  text?.search(getFullName(user)) - 1
);

export const putCaretInCorrectPosition = (
  { user, tagUserStartPosition, action },
  input,
  startPositionSelectedText,
  lastKey,
  selectedText = '',
) => {
  if (action === ActionTagUser.ADDUSER) {
    const nameUser = getFullName(user);
    const positionCursor = nameUser.length + tagUserStartPosition + 1;
    input.setSelectionRange(positionCursor, positionCursor);
  }
  else {
    if (!!selectedText) {
      if (lastKey === KEYS.BACKSPACE) {
        input.setSelectionRange(startPositionSelectedText, startPositionSelectedText);
      }
      else {
        input.setSelectionRange(startPositionSelectedText + 1, startPositionSelectedText + 1);
      }
    }
    else {
      input.setSelectionRange(tagUserStartPosition, tagUserStartPosition);
    }
  }
};

export const getTaggedUsers = (users: User[]) => {
  const taggedUsers: TaggedUser[] = [];

  users?.forEach((user) => {
    !!user && taggedUsers.push(getTaggedUser(user));
  });

  return taggedUsers;
};

export const getTaggedUser = (user: User, start: number = 0) => {
  if (!user) return null;

  return {
    start,
    length: getTaggedUserLength(user),
    type: {
      user: {
        id: user?.id,
      },
    },
  } as TaggedUser;
};

export const getTaggedUserLength = (user: User) => {
  if (!user) return 0;

  return user.firstName.length + user.lastName.length + 2;
};


export const getQueryParamsAutocompleteUser = (
  debouncedQuery,
  needSubordinates,
  needIssuerRequests
) => {
  const { user } = useAuth();

  if (needIssuerRequests) {
    return {
      queryFn: () =>
        getIssuerRequestsList(debouncedQuery, PAGE, LIMIT, true),
      queryKey: vacationsKeys.issuerList(debouncedQuery),
    };
  }

  if (needSubordinates) {
    return {
      queryFn: () =>
        getUserListSubordinatesForVacations(user?.id, debouncedQuery, PAGE, LIMIT, false),
      queryKey: profileKeys.userListSubordinates(debouncedQuery),
    };
  }

  return {
    queryFn: () => getUserList(debouncedQuery, PAGE, LIMIT, false),
    queryKey: profileKeys.userList(debouncedQuery),
  };
};
